import { useEffect, useRef, useState } from 'react';
import { PENDING_ACTION_TTL_MS, useAiScreenAction } from 'contexts/AiScreenActionsContext';

import type { TAiScreenAction } from 'utils/interfaces/IAiScreenAction';
import type {
  IPendingAgentSessionOpen,
  IUseValueChainAgentSessionAiActionParams,
} from '../interfaces/IUseValueChainAgentSessionAiAction';

/**
 * Answers the assistant's `openAgentSession` action on the value chain tab: opens an N-tier
 * discovery session, in the state it is in (`onOpenSession` decides: unit selection or modal).
 *
 * The chat made or changed the session on the backend, so the list the tab holds can be old, or
 * not there yet when the action made the app navigate to the map. The action is kept until a
 * sessions list read after it arrives and the board is laid out; the session then opens as that
 * list has it, its status included, with its units read from the board. A session the list does
 * not hold opens nothing.
 */
const useValueChainAgentSessionAiAction = ({
  valueChainMapId,
  agentSessions,
  isAgentSessionsFetching,
  agentSessionsFulfilledTimeStamp,
  refetchAgentSessions,
  isBoardReady,
  onOpenSession,
}: IUseValueChainAgentSessionAiActionParams): void => {
  const [pending, setPending] = useState<IPendingAgentSessionOpen | null>(null);
  const refetchAgentSessionsRef = useRef(refetchAgentSessions);
  refetchAgentSessionsRef.current = refetchAgentSessions;

  useAiScreenAction((action: TAiScreenAction): boolean => {
    if (action.type !== 'openAgentSession' || action.valueChainMapId !== Number(valueChainMapId)) {
      return false;
    }

    setPending({ sessionId: action.sessionId, subType: action.subType, requestedAt: Date.now() });

    return true;
  });

  useEffect(() => {
    if (pending) {
      Promise.resolve(refetchAgentSessionsRef.current()).catch(() => {});
    }
  }, [pending]);

  useEffect(() => {
    if (!pending) {
      return;
    }

    // A list that never arrives — the read failed, say — must not open the modal much later.
    if (Date.now() - pending.requestedAt > PENDING_ACTION_TTL_MS) {
      setPending(null);
      return;
    }

    if (
      !isBoardReady
      || isAgentSessionsFetching
      || (agentSessionsFulfilledTimeStamp ?? 0) < pending.requestedAt
    ) {
      return;
    }

    setPending(null);

    const session = agentSessions?.find(({ id }) => id === pending.sessionId);

    if (session) {
      onOpenSession({ ...session, subType: session.subType ?? pending.subType });
    }
  }, [
    agentSessions,
    agentSessionsFulfilledTimeStamp,
    isAgentSessionsFetching,
    isBoardReady,
    onOpenSession,
    pending,
  ]);
};

export default useValueChainAgentSessionAiAction;
